/**
 * useAgreements - Hook for saving legal agreements via edge function
 *
 * Calls the update-agreements edge function (service_role) instead of
 * writing directly to public.users — the authenticated role has no
 * UPDATE grant after the column-access migration.
 */

import { useState } from 'react';
import { supabase } from '../lib/supabase';

export interface Agreements {
  timestamp: string;
  version: string;
  professionalAcknowledgment: boolean;
  termsAndPrivacy: boolean;
  marketingOptIn: boolean;
}

export function useAgreements() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Save agreements via update-agreements edge function
   */
  const saveAgreements = async (agreements: Agreements): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        'update-agreements',
        {
          body: {
            professionalAcknowledgment: agreements.professionalAcknowledgment,
            termsAndPrivacy: agreements.termsAndPrivacy,
            marketingOptIn: agreements.marketingOptIn,
            version: agreements.version,
            timestamp: agreements.timestamp,
          },
        }
      );

      if (fnError) {
        throw fnError;
      }

      if (data && !data.success) {
        throw new Error(data.message || 'Failed to save agreements');
      }

      console.log('[useAgreements] Agreements saved via edge function:', {
        timestamp: agreements.timestamp,
        version: agreements.version,
      });

      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('[useAgreements] Failed to save agreements:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save agreements';
      setError(errorMessage);
      setIsLoading(false);
      return false;
    }
  };

  return {
    saveAgreements,
    isLoading,
    error,
  };
}
